import React, { useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';

const fmtOMR = (v) =>
  v == null ? '0' : Number(v)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const AIInsightPanel = ({ data, type = 'monthly', loading: dataLoading = false }) => {
  const [insight, setInsight] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);

  const generateInsight = useCallback(async () => {
    if (!data || dataLoading) return;
    setGenerating(true);
    setError('');
    try {
      const isMonthly = type === 'monthly';

      const systemPrompt = `You are a senior business analyst writing concise executive-level business insights for a management dashboard. 
Write in ${isMonthly ? '2-3' : '3-4'} sentences maximum. Use specific numbers from the data provided. 
Avoid generic phrases like "performance was good". Focus on concrete observations and actionable insights.
Write in professional third-person business language suitable for board-level reporting.`;

      const userPrompt = isMonthly
        ? `Generate a professional monthly executive business insight based on this data:
- Revenue: OMR ${fmtOMR(data?.totalRevenue)}
- Profit: OMR ${fmtOMR(data?.totalProfit)}
- Profit Margin: ${data?.profitMargin?.toFixed(1) || 0}%
- Closed Deals: ${data?.closedDeals || 0}
- Open Quotations: ${data?.openQuotations || 0}
- Active Projects: ${data?.activeProjects || 0}
- Outstanding Payments: OMR ${fmtOMR(data?.outstandingPayments)}
- New Customers: ${data?.newCustomers || 0}
- Previous Month Revenue: OMR ${fmtOMR(data?.prevRevenue)}
- Top Department: ${data?.topDepartment || 'N/A'}
- Delayed Projects: ${data?.delayedProjects || 0}
- Top Customer: ${data?.topCustomer || 'N/A'}

Write 2-3 sentences of executive-level insight. Include specific numbers. Professional tone. No bullet points.`
        : `Generate a professional yearly executive business summary based on this data:
- Total Revenue YTD: OMR ${fmtOMR(data?.totalRevenue)}
- Total Profit YTD: OMR ${fmtOMR(data?.totalProfit)}
- Profit Margin: ${data?.profitMargin?.toFixed(1) || 0}%
- Total Closed Deals: ${data?.closedDeals || 0}
- Active Customers: ${data?.activeCustomers || 0}
- Average Monthly Revenue: OMR ${fmtOMR(data?.avgMonthlyRevenue)}
- Best Month: ${data?.bestMonth || 'N/A'}
- Most Profitable Department: ${data?.topDepartment || 'N/A'}
- Top Customer: ${data?.topCustomer || 'N/A'}
- Open Quotations: ${data?.openQuotations || 0}

Write 3-4 sentences of executive-level annual summary. Include specific numbers. Professional tone. No bullet points.`;

      const messagesList = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ];
      const callOptions = { max_tokens: 250, temperature: 0.4 };

      const response = await getChatCompletion('OPEN_AI', 'gpt-4o-mini', messagesList, callOptions);

      if (response?.error) {
        throw new Error(response.error);
      }

      const text = response?.choices?.[0]?.message?.content || response?.content || '';

      if (text?.trim()) {
        setInsight(text?.trim());
        setGenerated(true);
      } else {
        setError('No insight generated. Please try again.');
      }
    } catch (err) {
      console.error('AI insight error:', err);
      const msg = err?.message || '';
      if (msg?.includes('401') || msg?.includes('authentication') || msg?.includes('Unauthorized')) {
        setError('AI service authentication failed. Please contact your administrator to update the API key.');
      } else if (msg?.includes('429')) {
        setError('AI service rate limit reached. Please wait a moment and try again.');
      } else if (msg?.includes('Failed to fetch') || msg?.includes('NetworkError') || msg?.includes('network')) {
        setError('Network error: Unable to reach the AI service. Please check your connection and try again.');
      } else if (msg?.includes('API key') || msg?.includes('not configured')) {
        setError('AI service is not configured. Please contact your administrator.');
      } else {
        setError('Failed to generate insight. Please check your connection and try again.');
      }
    } finally {
      setGenerating(false);
    }
  }, [data, type, dataLoading]);

  const isMonthly = type === 'monthly';
  const title = isMonthly ? 'Smart Business Insights' : 'Executive Annual Comment';
  const icon = isMonthly ? 'Sparkles' : 'Award';
  const description = isMonthly
    ? 'AI-generated executive summary from this month\'s performance data'
    : 'AI-generated annual executive summary based on year-to-date performance';

  return (
    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 border border-violet-200 dark:border-violet-800/50 rounded-2xl p-5">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-violet-100 dark:bg-violet-900/50 rounded-xl p-2.5">
            <Icon name={icon} size={17} className="text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">{title}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
          </div>
        </div>
        <button
          onClick={generateInsight}
          disabled={generating || dataLoading}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-xl transition-colors flex-shrink-0"
        >
          {generating ? (
            <>
              <Icon name="Loader2" size={12} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Icon name="Zap" size={12} />
              {generated ? 'Regenerate' : 'Generate'}
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl px-3 py-2.5 mb-3">
          <Icon name="AlertCircle" size={13} />
          {error}
        </div>
      )}

      {!insight && !error && !generating && (
        <div className="text-center py-6 text-muted-foreground">
          <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-2.5">
            <Icon name="MessageSquare" size={18} className="text-violet-400" />
          </div>
          <p className="text-sm font-medium">Click "Generate" to create an AI-powered executive insight</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Powered by GPT-4o — uses actual business data</p>
        </div>
      )}

      {generating && (
        <div className="flex items-center gap-3 py-5 px-2">
          <div className="flex gap-1">
            {[0, 1, 2]?.map(i => (
              <div
                key={i}
                className="w-2 h-2 bg-violet-500 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">Analyzing business data and generating insights...</span>
        </div>
      )}

      {insight && !generating && (
        <div className="bg-white/70 dark:bg-card/60 rounded-xl p-4 border border-violet-200/50 dark:border-violet-800/30">
          <p className="text-sm text-foreground leading-relaxed">"{insight}"</p>
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-violet-100 dark:border-violet-800/30">
            <Icon name="Sparkles" size={11} className="text-violet-500" />
            <span className="text-[10px] text-muted-foreground">AI-generated from actual system data · GPT-4o</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInsightPanel;
